import React from 'react';
import {
  CmsContentPage,
  CmsCountry,
  CmsDestination,
  CmsMemory
} from '../integrations/contentful/types';
import {
  fetchContentPage,
  fetchCountries,
  fetchDestinations,
  fetchMemories
} from '../integrations/contentful/queries';
import Container from '../components/grid/Container';
import CountryCard from '../components/countries/CountryCard';
import { t } from '../utils/i18n';
import Spacing from '../components/common/Spacing';
import SEO from '../components/head/SEO';
import { generatePath } from '../urls/generatePath';
import { Paths } from '../urls/Paths';
import Row from '../components/grid/Row';
import Col from '../components/grid/Col';
import { navbarLayout } from '../layouts/navbarLayout';
import H from '../components/elements/H';
import Breadcrumbs, { BreadcrumbItem } from '../components/head/Breadcrumbs';
import StickyCol from '../components/grid/StickyCol';
import DisplayOn from '../components/common/DisplayOn';
import { columnTopSpacing } from '../styles/theme';
import LabelledCount from '../components/list-pages/LabelledCount';
import LabelledCountRow from '../components/list-pages/LabelledCountRow';
import ContentBlockList from '../components/content-blocks/ContentBlockList';
import HorizontalLine from '../components/common/HorizontalLine';
import TimMessage from '../components/tim/TimMessage';
import { TimPage } from './TimPage';

interface CountryListPageProps {
  countries: CmsCountry[];
  memories: CmsMemory[];
  destinations: CmsDestination[];
  contentPage?: CmsContentPage;
}

export const countryListPageBreadcrumbItem: BreadcrumbItem = {
  name: t('Kaupungit'),
  path: Paths.CountryList
};

function renderLabelledCounts(
  countriesCount: number,
  tripsCount: number,
  destinationsCount: number
) {
  return (
    <LabelledCountRow>
      <LabelledCount
        data-testid="countries-count"
        count={countriesCount}
        label={t('kaupunkia')}
      />
      <LabelledCount
        data-testid="trips-count"
        count={tripsCount}
        label={t('golfmatkaa')}
      />
      <LabelledCount
        data-testid="destinations-count"
        count={destinationsCount}
        label={t('kenttää')}
      />
    </LabelledCountRow>
  );
}

const COUNTRY_INTRODUCTION_MESSAGE = t(
  'Täältä löydät kaikki kaupungit, joihin teemme golfmatkoja. 🌍'
);

const CountryListPage: TimPage<CountryListPageProps> = ({
  countries,
  memories,
  destinations,
  contentPage
}) => {
  if (!contentPage) {
    // ...
  }

  const tripsCount = memories.length;
  const destinationsCount = destinations.length;

  return (
    <div data-cy="country-list-page">
      <SEO
        title={t('Kaupungit')}
        canonicalPath={generatePath(Paths.CountryList)}
        description={t('Kaupungit')}
      />
      <Breadcrumbs items={[countryListPageBreadcrumbItem]} />
      <Container>
        <Spacing bottom={10}>
          <Row>
            <Col size={[24, 24, 7]}>
              <StickyCol contentTopOffset={columnTopSpacing.sideColumn}>
                <DisplayOn large>
                  <H styling="h2">
                    <strong>{t('Kaupungit')}</strong>
                  </H>
                  <Spacing top={2.5}>
                    {renderLabelledCounts(
                      countries.length,
                      tripsCount,
                      destinationsCount
                    )}
                  </Spacing>
                  <Spacing top={5}>
                    <TimMessage>{COUNTRY_INTRODUCTION_MESSAGE}</TimMessage>
                  </Spacing>
                </DisplayOn>
              </StickyCol>
            </Col>
            <Col size={[0, 0, 1]}></Col>
            <Col size={[24, 24, 16]}>
              <Row>
                <Col size={24}>
                  <DisplayOn small medium>
                    {renderLabelledCounts(
                      countries.length,
                      tripsCount,
                      destinationsCount
                    )}
                    <Spacing top={4}>
                      <TimMessage>{COUNTRY_INTRODUCTION_MESSAGE}</TimMessage>
                    </Spacing>
                  </DisplayOn>
                  <Spacing top={columnTopSpacing.contentColumn} bottom={5}>
                    {/* Add any other header content or filters here if needed */}
                  </Spacing>
                </Col>
              </Row>
              <Row>
                {countries.map((country, index) => {
                  return (
                    <Col data-testid="country" key={index} size={[12, 6, 6]}>
                      <CountryCard
                        mainImage={country.fields.mainImage}
                        name={country.fields.title}
                        slug={country.fields.slug}
                      />
                    </Col>
                  );
                })}
              </Row>
            </Col>
          </Row>
        </Spacing>
      </Container>
      <HorizontalLine expand={false} />
      <Spacing top={[8, 14, 14]} bottom={14}>
        {contentPage?.fields.blocks && (
          <ContentBlockList blocks={contentPage.fields.blocks} />
        )}
      </Spacing>
    </div>
  );
};

CountryListPage.getLayout = (page) =>
  navbarLayout(
    <DisplayOn small medium>
      <H styling="h4" tag="div">
        <strong>{t('Kaupungit')}</strong>
      </H>
    </DisplayOn>,
    page
  );

CountryListPage.getInitialProps = async function () {
  const [contentPage, countries, memories, destinations] = await Promise.all([
    // Reuse 'matkakohteet' content page for now to get some blocks,
    // or fetch a specific one if created like 'maat'
    fetchContentPage('matkakohteet'),
    fetchCountries(),
    fetchMemories(),
    fetchDestinations()
  ]);

  return {
    contentPage,
    countries: countries || [],
    memories: memories || [],
    destinations: destinations || []
  };
};

export default CountryListPage;
